import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button, Card, Checkbox, Label, Text } from '@/design-system/components/ui';

type StepContentType = {
  title: string;
  body: string;
  imageSrc?: string;
};

interface OnboardingStepProps {
  title: string;
  steps: StepContentType[];
  storageKey: string;
}

const LOCAL_STORAGE_KEY = 'onboardingDismissed';

export const FloatingInfoCard = ({ title, steps, storageKey }: OnboardingStepProps) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const isLastStep = currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;
  const currentStep = steps[currentStepIndex];

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const dismissedRaw = localStorage.getItem(LOCAL_STORAGE_KEY);
    const dismissedMap = dismissedRaw ? JSON.parse(dismissedRaw) : {};

    if (!dismissedMap[storageKey]) {
      setIsVisible(true);
      setTimeout(() => setAnimateIn(true), 50);
    }
  }, [storageKey]);

  const dismissForever = () => {
    if (typeof window === 'undefined') return;

    const dismissedRaw = localStorage.getItem(LOCAL_STORAGE_KEY);
    const dismissedMap = dismissedRaw ? JSON.parse(dismissedRaw) : {};
    dismissedMap[storageKey] = true;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dismissedMap));
  };

  const closeWithAnimation = (persist: boolean) => {
    if (persist) dismissForever();
    setAnimateIn(false);
    setTimeout(() => setIsVisible(false), 400);
  };

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      closeWithAnimation(dontShowAgain);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    closeWithAnimation(dontShowAgain);
  };

  if (!isVisible) return null;

  return createPortal(
    <Card
      className="tw:flex tw:flex-col tw:py-4 tw:w-[350px] tw:fixed tw:right-0 tw:bottom-0 tw:m-4 tw:z-[9999]"
      style={{
        opacity: animateIn ? 1 : 0,
        transform: animateIn ? 'translateY(0)' : 'translateY(40px)',
        transition: 'opacity 0.4s ease, transform 0.4s ease',
      }}
    >
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:px-2">
          <Text variant="title-xs" weight="bold">
            {title}
          </Text>
          <Button variant="ghost" size="icon" onClick={handleSkip}>
            <X />
          </Button>
        </div>

        <div
          className="tw:w-full tw:h-[250px] tw:overflow-hidden tw:relative"
          style={{
            backgroundColor: currentStep.imageSrc ? 'transparent' : '#e0e0e0',
          }}
        >
          {currentStep.imageSrc && (
            <Image
              src={currentStep.imageSrc}
              alt={currentStep.title}
              fill
              style={{
                objectFit: 'cover',
              }}
            />
          )}
        </div>

        <div className="tw:px-2">
          <Text variant="body-md" weight="bold" className="tw:mb-1">
            {currentStep.title}
          </Text>
          <Text variant="body-md" color="muted">
            {currentStep.body}
          </Text>
        </div>

        <div className="tw:flex tw:flex-col tw:px-2">
          <div className="tw:flex tw:items-center tw:gap-2">
            <Checkbox
              id="dont-show-again"
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked === true)}
            />
            <Label htmlFor="dont-show-again">
              <Text variant="caption">Don&apos;t show this tutorial again</Text>
            </Label>
          </div>

          <div className="tw:flex tw:flex-row tw:justify-between tw:items-center">
            <Button variant="ghost" size="sm" onClick={handleSkip}>
              <Text variant="caption">Skip the tutorial</Text>
            </Button>

            <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
              {!isFirstStep && (
                <Button variant="ghost" size="icon" onClick={handlePrev}>
                  <ArrowLeft />
                </Button>
              )}
              {isLastStep ? (
                <Button variant="ghost" size="sm" onClick={handleNext}>
                  Close
                </Button>
              ) : (
                <Button variant="ghost" size="icon" onClick={handleNext}>
                  <ArrowRight />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>,
    document.body,
    'FloatingInfoCard',
  );
};
